'use client';

import { useCallback } from 'react';

import { useGoogleMaps } from '@/app/(main)/(pages)/(home)/_components/location/_hooks/use-google-maps.hook';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { findKnownCityPlace } from '@/app/(main)/(pages)/(home)/_consts/known-city-places.const';
import { getLogger } from '@/logger/logger';
import { setUserLocation } from '@/store/user-location.store';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { Place } from '@/types/google-maps.types';
import { toCitySlug } from '@/utils/formatters/city/to-city-slug.util';
import { GOOGLE_MAPS_USAGE_LIMIT_MESSAGE, getGoogleMapsFailureReason } from '@/app/(main)/(pages)/(home)/_components/location/_utils/google-maps-error.util';

const logger = getLogger('search.city-location');

interface UseCityLocationReturn {
  isGoogleMapsReady: boolean;
  selectCity: (city: string, state: string) => Promise<Place | null>;
}

export function useCityLocation(): UseCityLocationReturn {
  const { isLoaded, loadError, getGoogleMapsPlaceByCityAndState } = useGoogleMaps();
  const { setParams } = useSearchParams([PersistedUserNavigationSearchParams.CIDADE]);

  const selectCity = useCallback(
    async (city: string, state: string): Promise<Place | null> => {
      try {
        const place = findKnownCityPlace(city, state) ?? await getGoogleMapsPlaceByCityAndState(city, state);
        const citySlug = toCitySlug(place?.city, place?.state);

        if (!place || !citySlug) {
          logger.warn('Could not resolve a location for the city', {
            search: { event: 'city_location_unresolved', city, state },
          });

          return null;
        }

        setUserLocation(place);
        setParams({ [PersistedUserNavigationSearchParams.CIDADE]: citySlug });

        return place;
      } catch (error) {
        const reason = getGoogleMapsFailureReason(error);

        logger.error(reason ? GOOGLE_MAPS_USAGE_LIMIT_MESSAGE : 'Failed to resolve a location for the city', {
          search: { event: 'city_location_failed', city, state, reason },
          error,
        });

        return null;
      }
    },
    [getGoogleMapsPlaceByCityAndState, setParams],
  );

  return { isGoogleMapsReady: isLoaded || Boolean(loadError), selectCity };
}
